import BlogArticle3up from '@/components/BlogComponents/BlogArticle3Up/BlogArticle3Up';
import BlogCategorySplit from '@/components/BlogComponents/BlogCategorySplit/BlogCategorySplit';
import BlogCuratedContentSection from '@/components/BlogComponents/BlogCuratedContentSection/BlogCuratedContentSection';
import BlogHighlight from '@/components/BlogComponents/BlogHighlight/BlogHighlight';
import BlogHubHero from '@/components/BlogComponents/BlogHubHero/BlogHubHero';
import StaticSchema from '@/components/Schema/StaticSchema';
import { Section, SectionType } from '@/components/Section/Section';
import {
  BlogCategoryPageContentType,
  GlobalFooterContent,
  GlobalNavContent,
} from '@/contentful';
import { userLocationInterface } from '@/hooks/geo';
import { BlogNavItemCf } from '@/types';
import { isDefined } from '@/types/typeGuards';

import { FooterComponent, HeaderComponent } from './StandardPage';

interface Props extends userLocationInterface {
  content: BlogCategoryPageContentType;
}

export default function BlogCategoryPage({
  content: {
    fields: {
      header,
      eyebrow,
      heading,
      subheading,
      category,
      categoryNav,
      sections,
      footer,
      linkedArticleContent,
      dynamicBlogContent,
    },
  },
  userLocation,
  setUserLocation,
}: Props) {
  const curatedBlogContent =
    linkedArticleContent?.curatedBlogContentCollection?.items;
  const featuredArticles =
    linkedArticleContent?.featuredArticlesCollection?.items;
  const categoryNavigation = categoryNav?.fields
    ?.categories as unknown as BlogNavItemCf[];
  const allCategoriesUrl = categoryNav?.fields?.allCategoriesUrl;
  const blogCategory = category?.fields?.categoryName;
  const blogHighlightBlockPrimaryArticle =
    linkedArticleContent?.blogHighlightBlockPrimaryArticle;
  const blogHighlightBlockArticles =
    linkedArticleContent?.blogHighlightBlockArticlesCollection?.items || [];

  return (
    <>
      <StaticSchema />
      {header && (
        <HeaderComponent
          header={header as unknown as GlobalNavContent}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
        />
      )}
      {featuredArticles && (
        <BlogHubHero
          eyebrow={eyebrow}
          heading={heading}
          subheading={subheading}
          categoryNav={categoryNavigation}
          allCategoriesUrl={allCategoriesUrl}
          category={blogCategory}
          featuredArticles={featuredArticles}
        />
      )}
      {curatedBlogContent && (
        <BlogCuratedContentSection curatedBlogContent={curatedBlogContent} />
      )}
      {(blogHighlightBlockPrimaryArticle ||
        blogHighlightBlockArticles.length > 0) && (
        <BlogHighlight
          primaryArticle={blogHighlightBlockPrimaryArticle}
          articlesCollection={blogHighlightBlockArticles}
        />
      )}
      {dynamicBlogContent?.map((dynamicSection, idx) => {
        // type narrowing DynamicArticleGridType
        if (dynamicSection.contentTypeName === 'DynamicArticleGrid') {
          return (
            <BlogArticle3up
              key={`grid-${category?.fields.categoryName || 'category'}-${idx}`}
              blogCategory={category?.fields.categoryName}
              heading={dynamicSection.heading}
              initialArticles={dynamicSection.items}
            />
          );
        } else {
          return (
            <BlogCategorySplit
              key={`split-${
                category?.fields.categoryName || 'category'
              }-${idx}`}
              articleColumn1={dynamicSection?.column1Articles}
              articleColumn2={dynamicSection?.column2Articles}
              categoryTitle1={dynamicSection?.column1Heading}
              categoryTitle2={dynamicSection?.column2Heading}
              column1CategoryUrl={dynamicSection?.column1CategoryUrl}
              column2CategoryUrl={dynamicSection?.column2CategoryUrl}
            />
          );
        }
      })}
      {sections &&
        sections
          .filter(isDefined)
          .map((section) => (
            <Section
              key={section.sys.id}
              section={section as unknown as SectionType}
              userLocation={userLocation}
            />
          ))}
      {footer && (
        <FooterComponent
          footer={footer as unknown as GlobalFooterContent}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
        />
      )}
    </>
  );
}
